import React, { useEffect, useMemo, useState } from "react";
import { api } from "../api";

function fmt(n) {
  const v = Number(n || 0);
  return (v < 0 ? "-$" : "$") + Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function isoDate(d) { return d.toISOString().slice(0, 10); }

function presetRange(key) {
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth();
  if (key === "this_month") return { from: isoDate(new Date(y, m, 1)), to: isoDate(new Date(y, m + 1, 0)) };
  if (key === "last_month") return { from: isoDate(new Date(y, m - 1, 1)), to: isoDate(new Date(y, m, 0)) };
  if (key === "this_quarter") { const q = Math.floor(m / 3); return { from: isoDate(new Date(y, q * 3, 1)), to: isoDate(new Date(y, q * 3 + 3, 0)) }; }
  if (key === "this_year") return { from: isoDate(new Date(y, 0, 1)), to: isoDate(new Date(y, 11, 31)) };
  return presetRange("this_month");
}

const PRESETS = [
  { key: "this_month", label: "This Month" },
  { key: "last_month", label: "Last Month" },
  { key: "this_quarter", label: "This Quarter" },
  { key: "this_year", label: "This Year" },
  { key: "custom", label: "Custom" },
];

export default function Reports() {
  const [preset, setPreset] = useState("this_month");
  const [range, setRange] = useState(presetRange("this_month"));
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (preset !== "custom") setRange(presetRange(preset));
  }, [preset]);

  useEffect(() => {
    setLoading(true);
    setError("");
    api.reports.profitLoss(range.from, range.to)
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [range.from, range.to]);

  const exportUrl = useMemo(() => api.reports.profitLossExportUrl(range.from, range.to), [range.from, range.to]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: "#154E93" }}>Reports</h1>
          <p className="text-sm text-gray-500">Profit &amp; Loss and other agency reports</p>
        </div>
        <a href={exportUrl} className="px-4 py-2 text-white rounded text-sm font-medium" style={{ backgroundColor: "#154E93" }}>
          Export CSV
        </a>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <h2 className="text-lg font-semibold mb-3" style={{ color: "#154E93" }}>Profit &amp; Loss Statement</h2>

        <div className="flex flex-wrap items-center gap-2 mb-4">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPreset(p.key)}
              className="px-3 py-1.5 rounded text-sm font-medium border"
              style={
                preset === p.key
                  ? { backgroundColor: "#154E93", color: "#fff", borderColor: "#154E93" }
                  : { borderColor: "#d1d5db", color: "#374151" }
              }
            >
              {p.label}
            </button>
          ))}
          {preset === "custom" && (
            <>
              <input
                type="date"
                value={range.from}
                onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
                className="border rounded px-2 py-1 text-sm"
              />
              <span className="text-sm text-gray-400">to</span>
              <input
                type="date"
                value={range.to}
                onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
                className="border rounded px-2 py-1 text-sm"
              />
            </>
          )}
        </div>

        <div className="text-xs text-gray-400 mb-3">
          {range.from} &mdash; {range.to}
        </div>

        {error && <div className="mb-4 text-sm bg-red-50 text-red-700 px-3 py-2 rounded">{error}</div>}
        {loading && <div className="text-sm text-gray-400 py-4">Loading…</div>}

        {!loading && data && (
          <div className="max-w-xl">
            <div className="mb-4">
              <div className="text-sm font-semibold text-gray-600 uppercase mb-1">Income</div>
              {data.income.map((r) => (
                <div key={r.account_code} className="flex items-center justify-between py-1 text-sm border-b last:border-0">
                  <span>{r.account_name}</span>
                  <span>{fmt(r.total)}</span>
                </div>
              ))}
              {data.income.length === 0 && <div className="text-sm text-gray-400 py-1">No income in this period.</div>}
              <div className="flex items-center justify-between py-1.5 text-sm font-semibold border-t mt-1" style={{ color: "#154E93" }}>
                <span>Total Income</span>
                <span>{fmt(data.income_total)}</span>
              </div>
            </div>

            <div className="mb-4">
              <div className="text-sm font-semibold text-gray-600 uppercase mb-1">Expenses</div>
              {data.expenses.map((r) => (
                <div key={r.account_code} className="flex items-center justify-between py-1 text-sm border-b last:border-0">
                  <span>{r.account_name}</span>
                  <span className="text-red-600">{fmt(r.total)}</span>
                </div>
              ))}
              {data.expenses.length === 0 && <div className="text-sm text-gray-400 py-1">No expenses in this period.</div>}
              <div className="flex items-center justify-between py-1.5 text-sm font-semibold border-t mt-1 text-red-600">
                <span>Total Expenses</span>
                <span>{fmt(data.expenses_total)}</span>
              </div>
            </div>

            <div
              className="flex items-center justify-between py-3 px-3 rounded text-base font-bold"
              style={{ backgroundColor: data.net_profit >= 0 ? "#EAF2FB" : "#FEF2F2", color: data.net_profit >= 0 ? "#154E93" : "#DC2626" }}
            >
              <span>Net Profit</span>
              <span>{fmt(data.net_profit)}</span>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-sm p-4">
        <h2 className="text-lg font-semibold mb-2" style={{ color: "#154E93" }}>More Reports (Coming Soon)</h2>
        <p className="text-sm text-gray-500">Client Reports, Caregiver Reports, Scheduling Reports, and a Custom Report Builder are planned next.</p>
      </div>
    </div>
  );
}
